/**
 * Frontend utilities for formatting and data transformation
 */

/**
 * Format percentage with symbol
 */
export function formatPercent(value: number, decimals: number = 0): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format hours as readable string
 */
export function formatHours(hours: number): string {
  if (hours === 0) return '0h';
  const days = Math.floor(hours / 8);
  const remainder = hours % 8;
  
  if (days > 0 && remainder > 0) {
    return `${days}d ${remainder}h`;
  } else if (days > 0) {
    return `${days}d`;
  }
  return `${remainder}h`;
}

/**
 * Format story points
 */
export function formatPoints(points: number): string {
  return `${points} sp`;
}

/**
 * Get color based on utilization percentage
 */
export function getUtilizationColor(utilizationPercent: number): string {
  if (utilizationPercent <= 75) {
    return '#22c55e'; // Green - under-allocated
  } else if (utilizationPercent <= 100) {
    return '#eab308'; // Yellow - at capacity
  } else if (utilizationPercent <= 120) {
    return '#f97316'; // Orange - slightly over
  }
  return '#ef4444'; // Red - significantly over
}

/**
 * Get status badge color
 */
export function getStatusColor(status: string): {
  bg: string;
  text: string;
  border: string;
} {
  switch (status) {
    case 'under-allocated':
      return {
        bg: 'bg-success-50',
        text: 'text-success-600',
        border: 'border-success-200',
      };
    case 'at-capacity':
      return {
        bg: 'bg-warning-50',
        text: 'text-warning-600',
        border: 'border-warning-200',
      };
    case 'over-allocated':
      return {
        bg: 'bg-danger-50',
        text: 'text-danger-600',
        border: 'border-danger-200',
      };
    default:
      return {
        bg: 'bg-primary-50',
        text: 'text-primary-600',
        border: 'border-primary-200',
      };
  }
}

/**
 * Format date range for display
 */
export function formatDateRange(start: Date, end: Date): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  });

  return `${formatter.format(start)} - ${formatter.format(end)}`;
}

/**
 * Calculate days between dates
 */
export function daysBetween(start: Date, end: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((end.getTime() - start.getTime()) / msPerDay);
}

/**
 * Parse ISO date string
 */
export function parseDate(dateString: string): Date {
  return new Date(dateString);
}

/**
 * Format date as ISO string (YYYY-MM-DD)
 */
export function formatDateISO(date: Date): string {
  return date.toISOString().split('T')[0];
}

/**
 * Calculate capacity status label
 */
export function getStatusLabel(utilizationPercent: number): string {
  if (utilizationPercent <= 75) {
    return 'Under-allocated';
  } else if (utilizationPercent <= 100) {
    return 'At capacity';
  }
  return 'Over-allocated';
}

/**
 * Round to specified decimal places
 */
export function round(value: number, decimals: number = 2): number {
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
}

/**
 * Calculate average
 */
export function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * Get initials from name
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}
